/**
 * WEEX REST API Functions
 *
 * REST endpoint helpers for WEEX v2 spot market data.
 *
 * Endpoints/params/shapes mirror the production WEEX connector
 * (gitlab.1wq.net/mm/111-connman → connector/weex/market/rest/service.go).
 *
 * @see https://api-spot.weex.com
 */

import type {
  WeexDepthRequest,
  WeexDepthResponse,
  WeexKlinesRequest,
  WeexKlinesResponse,
  WeexSymbolsRequest,
  WeexSymbolsResponse,
  WeexTickerRequest,
  WeexTickerResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api-spot.weex.com';

/**
 * WEEX API success code.
 */
export const WEEX_SUCCESS_CODE = '00000';

/**
 * WEEX REST API endpoints.
 * @see connector/weex/market/rest/service.go
 */
export const weexRestEndpoints = {
  ticker: `${BASE_URL}/api/v3/market/ticker/24hr`,
  symbols: `${BASE_URL}/api/v3/exchangeInfo`,
  depth: `${BASE_URL}/api/v3/market/depth`,
  klines: `${BASE_URL}/api/v3/market/klines`,
} as const;

/**
 * Format a base/quote pair into a WEEX v3 symbol (plain BTCUSDT).
 */
export function formatWeexSymbol(base: string, quote: string): string {
  return `${base}${quote}`.toUpperCase();
}

/**
 * Parse a WEEX symbol like "BTCUSDT_SPBL" into base/quote.
 * Requires known quote currencies as WEEX doesn't use separators.
 *
 * @example
 * parseWeexSymbol('BTCUSDT_SPBL') // { base: 'BTC', quote: 'USDT' }
 */
export function parseWeexSymbol(
  symbol: string,
  knownQuotes: string[] = ['USDT', 'USDC', 'TUSD', 'FDUSD', 'BTC', 'ETH', 'BNB', 'TRY', 'EUR'],
): { base: string; quote: string } | null {
  const upper = symbol.toUpperCase().replace(/_SPBL$/, '');
  const sortedQuotes = [...knownQuotes].sort((a, b) => b.length - a.length);

  for (const quote of sortedQuotes) {
    if (upper.endsWith(quote)) {
      const base = upper.slice(0, -quote.length);
      if (base.length > 0) {
        return { base, quote };
      }
    }
  }

  return null;
}

/**
 * Fetches symbol information including tick size / step size / min trade amount.
 * WEEX returns the data as an array even for a single-symbol query.
 * @see https://api-spot.weex.com/api/v2/public/exchangeInfo
 */
export const getWeexSymbols = async (
  params?: WeexSymbolsRequest,
  options?: RestRequestOptions,
): Promise<WeexSymbolsResponse> => {
  const response = await get<WeexSymbolsResponse, WeexSymbolsRequest | undefined>({
    url: weexRestEndpoints.symbols,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://api-spot.weex.com/api/v2/market/ticker
 */
export const getWeexTicker = async (
  params: WeexTickerRequest,
  options?: RestRequestOptions,
): Promise<WeexTickerResponse> => {
  const response = await get<WeexTickerResponse, WeexTickerRequest>({
    url: weexRestEndpoints.ticker,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches orderbook depth for a symbol.
 *
 * IMPORTANT: WEEX rejects a `limit` query param with code 40015, so this helper
 * intentionally sends only the symbol.
 * @see https://api-spot.weex.com/api/v2/market/depth
 */
export const getWeexDepth = async (
  params: WeexDepthRequest,
  options?: RestRequestOptions,
): Promise<WeexDepthResponse> => {
  const response = await get<WeexDepthResponse, WeexDepthRequest>({
    url: weexRestEndpoints.depth,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches candlestick (kline) data for a symbol.
 *
 * WEEX uses a `period` param (1m/5m/.../1M) and always returns ~300 candles.
 * Time-range params (startTime/endTime) are NOT supported.
 * @see https://api-spot.weex.com/api/v2/market/candles
 */
export const getWeexKlines = async (
  params: WeexKlinesRequest,
  options?: RestRequestOptions,
): Promise<WeexKlinesResponse> => {
  const response = await get<WeexKlinesResponse, WeexKlinesRequest>({
    url: weexRestEndpoints.klines,
    params,
    options,
  });

  return response.data;
};
